import { englishTranslations } from './englishTranslations';
import { frenchTranslations } from './frenchTranslations';
import { italianTranslations } from './italianTranslations';
import { portugueseTranslations } from './portugueseTranslations';
import { spanishTranslations } from './spanishTranslations';

export const appText: TranslationDictionary = {
  enUS: englishTranslations,
  frFR: frenchTranslations,
  esES: spanishTranslations,
  ptBR: portugueseTranslations,
  itIT: italianTranslations,
};

interface ConfirmationModalText {
  header?: string;
  confirmationMessage?: string;
  confirmationButtonText?: string;
  cancelButtonText?: string;
  successMessage?: string;
}

// For screens shown before the user's preferred language is known (e.g. UserLoadError): pick the
// first browser language we have translations for, by its two-letter code, falling back to English.
export function getBrowserTranslations(
  languages: readonly string[] = typeof navigator === 'undefined' ? [] : navigator.languages
): Translations {
  for (const language of languages) {
    const code = language.substring(0, 2).toLowerCase();
    const key = Object.keys(appText).find(
      (locale) => locale.substring(0, 2).toLowerCase() === code
    );
    if (key != null) {
      return appText[key];
    }
  }

  return appText['enUS'];
}

export interface TranslationDictionary {
  [key: string]: Translations;
}

export interface Translations {
  admin: {
    applicationCyclesCurrent: string;
    applicationCyclesHeader: string;
    applicationCyclesPast: string;
    applicationCyclesUpcoming: string;
    applicationsHeader: string;
    emailsHeader: string;
    tabEmailActivity: string;
    tabSendEmail: string;
    tabTcowMassApproval: string;
  };
  address: {
    addAddress2: string;
    labelCity: string;
    labelCountry: string;
    labelCounty: string;
    labelState: string;
    labelStreetAddress: string;
    labelStreetAddress2: string;
    labelZipCode: string;
  };
  applicationBody: {
    approveApplicationButton: string;
    resubmitButton: string;
    sendBackToApplicantWithComments: string;
    starApprovalButton: string;
    starRemovalButton: string;
    submitButton: string;
    submitMessage: string;
    tooltipApproveSendsToNextReviewStage: string;
    tooltipApproveBeforeSubmit: string;
    tooltipApproveWithUnresolvedComments: string;
    tooltipNextStandard: string;
    tooltipPreviousStandard: string;
    tooltipRequiredQuestions: string;
    tooltipSendBackRequiresComment: string;
  };
  applicationHeader: {
    headerViewingApplication: string;
    messageRequiredQuestions: string;
    missingRequiredQuestions: string;
    reviewerComments: string;
    standardCompleted: string;
    starAwarded: string;
    yearsRecognized: (year: number) => string;
  };
  applicationPreview: {
    backToAdmin: string;
    previewModeMessage: string;
    programLabel: string;
    unknownValue: string;
  };
  common: {
    ariaSaved: string;
    ariaSaveFailed: string;
    ariaSaving: string;
    buttonPost: string;
    buttonReactivate: string;
    buttonResolve: string;
    defaultConfirmationModalText: ConfirmationModalText;
    buttonCancel: string;
    buttonClose: string;
    buttonContinue: string;
    buttonSaving: string;
    buttonSave: string;
    buttonDelete: string;
    buttonUpdate: string;
    characterCountRemaining: (remaining: number, max: number) => string;
    commentResolved: string;
    error: string;
    genericError: string;
    messageCommentAddedSuccessfully: string;
    messageCommentAddFailed: string;
    messageCommentCannotBeEmpty: string;
    messageCommentTooLong: string;
    messageGrowthAwardApprovalSuccess: string;
    messageGrowthAwardApproveProblem: string;
    messageGrowthAwardApproving: string;
    messageGrowthAwardCategoriesFetchError: string;
    messageGrowthAwardCommentRequired: string;
    messageGrowthAwardReturnSuccess: string;
    messageGrowthAwardSendBackProblem: string;
    messageGrowthAwardSendingBack: string;
    messageGrowthAwardSubmitted: string;
    messageGrowthAwardSubmitting: string;
    messageGrowthAwardSubmitProblem: string;
    noMatch: string;
    placeholderPostComment: string;
    previewInputPlaceholder: string;
    previewWorkflowSpecificDataNotice: string;
    requiredIndicatorTitle: string;
    statusApplicationApproved: string;
    statusApplicationReturned: string;
    statusApplicationSubmitted: string;
    statusApproving: string;
    statusReturning: string;
    statusUpdating: string;
    tooltipSubmitGrowthAward: string;
    tooltipRetrySave: string;
    tooltipSaveFailed: string;
  };
  contact: {
    confirmationModal: ConfirmationModalText;
    buttonAdd: string;
    buttonChange: string;
    buttonCreate: string;
    buttonSave: string;
    buttonUpdate: string;
    createContact: string;
    editContact: string;
    editWarning: (appYear: string) => string;
    invalidEmail: string;
    invalidZipCode: string;
    invalidPhoneNumber: string;
    requiredFieldSuffix: string;
    labelCity: string;
    labelCountry: string;
    labelEmail: string;
    labelFirstName: string;
    labelLastNameLabel: string;
    labelName: string;
    labelPhoneNumber: string;
    labelStreetAddress2Label: string;
    labelStreetAddressLabel: string;
    labelTitle: string;
    labelWhatsApp: string;
    labelZipCode: string;
    messageMaximum: string;
    messageSaved: string;
    messageContactRemoved: string;
    messageContactSaveFailed: string;
    placeHolderPhoneNumber: string;
    treeBoardLimitText?: (maxInstances: number) => string;
  };
  dashboard: {
    accessToolkit: string;
    accessToolkitPendingApproval: string;
    download: string;
    downloadTooltip: string;
    exportEmailLabel: string;
    exportEmailAlreadyAdded: (email: string) => string;
    exportEmailPlaceholder: string;
    exportErrorMessage: string;
    exportGenerating: string;
    exportGenerateCsv: string;
    exportIncludeQuestionResponses: string;
    exportInvalidEmailAddress: (email: string) => string;
    exportMaxEmailsWarning: string;
    exportModalTitle: string;
    exportOptionalParams: string;
    exportRequiredParams: string;
    exportSelectApplicationType: string;
    exportSelectCounty: string;
    exportSelectPartner: string;
    exportSelectProgram: string;
    exportSelectState: string;
    exportSuccessMessage: string;
    exportTypeGrowthAward: string;
    exportTypeMainApplications: string;
    exportTypeStateQuestions: string;
    fieldApplication: string;
    fieldApplicationCycle: string;
    fieldStatus: string;
    fieldSubmittedBy: string;
    growthAwardCardHeader: string;
    growthAwardCardPendingLabel: (programName: string) => string;
    growthAwardCardStartButton: string;
    growthAwardHeader: string;
    growthAwardPendingLabel: string;
    growthAwardStartButton: string;
    labelApplication: string;
    labelCreateApplication: string;
    labelPastApplications: string;
    loadingText: string;
    messageCreateApplication: string;
    messageCreateApplicationError: string;
    messageNoCycleFound: string;
    messageNoApplications: string;
    messageFetchApplicationsError: string;
    portalClosedInformation: (latestAppCycleYear: string, nextOpenDate?: string | null) => string;
    selectPartner: string;
    stateQuestionsCardHeader: string;
    stateQuestionsCardPendingLabel: string;
    stateQuestionsCardStartButton: string;
    stateQuestionsHeader: string;
    stateQuestionsPendingLabel: string;
    stateQuestionsStartButton: string;
    viewApplication: string;
    viewApplicationButton: string;
    viewApplicationHelperTextDisabled: string;
    viewApplicationHelperTextEnabled: string;
  };
  dataUpload: {
    confirmationModal: ConfirmationModalText;
    errorURL: string;
    helperSelectFile: string;
    helperUrl: string;
    labelAddFile: string;
    labelEventProof: string;
    labelFile: string;
    labelURL: string;
    labelSave: string;
    labelSaving: string;
    labelSelectFile: string;
    labelUpload: string;
    messageDownload: string;
    messageFailed: string;
    messageSuccess: string;
    placeholderURL: string;
    errorFileSize: string;
    errorFileTypeUnsupported: string;
    warningDuplicateFileSkipped: string;
    warningDuplicateUrlExists: string;
    labelTryAgain: string;
    labelUploadedFile: string;
    labelSavedUrl: string;
    labelGetPdfSummary: string;
    tooltipClearError: string;
    tooltipOpen: string;
    tooltipDownload: string;
    tooltipDelete: string;
    ariaClearUploadError: string;
    ariaOpenAttachment: string;
    ariaOpenUrlAttachment: string;
    ariaDownloadAttachment: string;
  };
  deadlineBanner: {
    deadlineText: string;
    stateReviewDeadlineText: string;
  };
  decimalQuestion: {
    maxValueExceeded: string;
  };
  event: {
    confirmationModal: ConfirmationModalText;
    buttonAddEvent: string;
    celebration: string;
    dateRangeError: string;
    eventName: string;
    helperEvent: string;
    labelName: string;
    labelSubType: string;
    labelSubTypeOther: string;
    messageDelete: string;
    messageSaved: string;
    other: string;
    summary: string;
    tooltipDelete: string;
    treeDay: string;
    type: string;
  };
  header: {
    application: string;
    dashboard: string;
    error: string;
    growthaward?: string;
    inactive: string;
    instructionsTitle: string;
    myaccount?: string;
    mycontact?: string;
    pending: string;
    pendingMessage: string;
    signedInAs: string;
    signup: string;
    statequestions?: string;
    tooltipDashboard: string;
    tooltipLanguage: string;
    tooltipHelp: string;
    tooltipMyAccount: string;
    tooltipSignOut: string;
    tooltipStopImpersonation: string;
    tooltipUserManagement: string;
    usermanagement: string;
  };
  integerQuestion: {
    invalidInteger: string;
    labelSum: string;
  };
  multiCheckbox: {
    helper: string;
  };
  multiCheckbox3: {
    helperMaximum: string;
  };
  myaccount: {
    accountSettings: string;
    buttonDiscardChanges: string;
    buttonKeepEditing: string;
    characterlimit: (charLimit: number) => string;
    errorHelperText: string;
    emailNewAccountHelperText: string;
    myAccount: string;
    preferredlanguage: string;
    primaryContactHeader: string;
    tabAccountSettings: string;
    tabPrimaryContact: string;
    tabShippingAddress: string;
    titlerequired: string;
    shippingContactHeader: string;
    errorUpdatingShippingContact: string;
    shippingContactUpdated: string;
    unsavedChangesBody: string;
    unsavedChangesTitle: string;
    updatePrimaryContactError: string;
    updatePrimaryContactSuccess: string;
    updateUserContactSuccess: string;
    updateUserContactError: string;
    partnerDetails: (partnerName: string) => string;
  };
  partner: {
    other: string;
  };
  partnerUpdateModal: {
    saveAwards: string;
    saveContacts: string;
    savePartnerInfo: string;
    tabContacts: string;
    tabPartnerInfo: string;
    tabYearsAwarded: string;
    titleEditPartnerDetails: string;
    tooltipError: string;
    tooltipHasUnsavedChanges: string;
    tooltipSaved: string;
    tooltipSaving: string;
  };
  profileMenu: {
    addAccount: string;
    addAccountTooltip: string;
    manageAccount: string;
    myAccount: string;
    signOut: string;
    admin: string;
    userManagement: string;
  };
  percentQuestion: {
    invalidPercent: string;
  };
  signature: {
    buttonClear: string;
    messageSaved: string;
    terms: string;
    tooShortError: string;
  };
  submitConfirmation: {
    title: string;
    message: string;
    reviewApplication: string;
    backToDashboard: string;
  };
  signup: {
    buttonSubmit: string;
    checkContactShipping: string;
    checkIncludeInDirectory: string;
    choiceNo: string;
    choiceSelect: string;
    choiceYes: string;
    countrySelect: string;
    fuzzyMatchContinue: (partnerTerm: string) => string;
    fuzzyMatchMessage: string;
    fuzzyMatchSelect: string;
    fuzzyMatchTitle: string;
    errorMessageEnd: string;
    errorMessageStart: string;
    errorPartnerHeader: string;
    errorPrimaryContactHeader: string;
    errorShippingContactHeader: string;
    headerExistingPartnerDetails: string;
    headerLabel: string;
    headerPartnerDetails: string;
    headerPrimaryContact: string;
    headerProgram: string;
    headerAddAccount: string;
    headerSelectProgram: string;
    headerShipping: string;
    headerSocialMedia: string;
    helperCityError: string;
    helperEmailError: string;
    helperNameError: string;
    helperShippingCityError: string;
    helperShippingStreetAddressError: string;
    helperShippingZipCodeError: string;
    helperStreetAddressError: string;
    helperZipCode: string;
    helperZipCodeError: string;
    labelAppliedToADF: (partnerTerm: string, programName: string) => string;
    labelCategory: string;
    labelCity: string | undefined;
    labelGenericEmail: string;
    labelName: string;
    labelPhoneNumber: string;
    labelShippingCity: string;
    labelShippingCountry: string;
    labelShippingStreetAddress: string;
    labelShippingStreetAddress2: string;
    labelShippingZipCode: string;
    labelSubCategoryOther: string;
    labelTitle: string;
    labelZipCode: string;
    messageSubmissionError: string;
    messageSubmissionSuccess: string;
    partnerNotFoundHelperText: (partnerTerm: string) => string;
    partnerSelect: string;
    required: string;
    selectProgram: string;
    subHeaderExistingPartnerDetails: (partnerTerm: string, programName: string) => string;
    subHeaderPartnerDetails: string;
    tooltipSubmit: string;
  };
  userLoadError: {
    contactUs: string;
    message: string;
    referenceLabel: string;
    signOut: string;
    title: string;
    tryAgain: string;
  };
  userManagement: {
    removeReviewerAssignmentConfirmationModal: ConfirmationModalText;
    snackbarError: string;
    snackbarSuccess: string;
  };
}
